import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Form, Offcanvas } from "react-bootstrap";
import AsyncSelect from "react-select/async";

import {
  MdVisibility,
  MdRefresh,
  MdVerified,
  MdEmail,
  MdPhone,
  MdLocationOn,
  MdDescription,
  MdAttachMoney,
  MdCalendarToday,
} from "react-icons/md";
import "../../styles/InputSheet.css";
import "../../styles/ReactSelect.css";
import "../../styles/FileUpload.css";
import { GoUnverified } from "react-icons/go";

import {
  FiPlusCircle,
  FiMinusCircle,
  FiUploadCloud,
  FiInfo,
} from "react-icons/fi";
import {
  addAlmataPartner,
  getAlmataPartners,
  editAlmataPartner,
  deleteAlmataPartner,
  changeAlmataStatus,
  resendAlmataVerificationEmail,
} from "../../redux/actions/almataActions";
import { getCountries } from "../../redux/actions/countryActions";
import DeleteConfirmModal from "../../modals/DeleteConfirmModal";
import StatusConfirmModal from "../../modals/StatusConfirmModal";
import InactiveEditModal from "../../modals/InactiveEditModal";
import PasswordResetModal from "../../modals/PasswordResetModal";
import LogoPreviewModal from "../../modals/LogoPreviewModal";
import Pagination from "../../../helper/Pagination";
import { toast } from "react-toastify";
import PlansDetails from "./common/PlansDetails";
import AlmataPartnerTable from "./common/AlmataPartnerTable";
import {
  getPhoneValidationRules,
  validatePhoneNumber,
} from "../../../utils/phoneValidation";

const AlmataPartner = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = sessionStorage.getItem("authToken");
  const {
    partners = [],
    loading = false,
    formLoading = false,
    error = null,
    pagination = {},
    passwordResetStatus = null,
  } = useSelector((state) => state.almata || {});
  const { countries = [] } = useSelector((state) => state.country || {});
  const [currentPage, setCurrentPage] = useState(1);

  const {
    handleSubmit,
    formState: { errors },
    reset,
    clearErrors,
    register,
    watch,
    control,
    setValue,
  } = useForm({
    defaultValues: {
      companyName: "",
      email: "",
      firstName: "",
      lastName: "",
      contactNumber: "",
      logo: null,
      country: "",
      currency: "",
      status: "active",
      almataSharedDiscription: "",
    },
  });

  const [showInputForm, setShowInputForm] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [editingPartner, setEditingPartner] = useState(null);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    partner: null,
  });
  const [statusModal, setStatusModal] = useState({
    isOpen: false,
    partner: null,
    newStatus: null,
  });
  const [searchTerm, setSearchTerm] = useState("");

  const handleSearchChange = (e) => {
    const value = e.target.value;
    if (value.length === 0 || (value.length > 0 && value[0] !== " ")) {
      setSearchTerm(value);
      setCurrentPage(1);
    } else if (searchTerm.length > 0) {
      setSearchTerm(value);
      setCurrentPage(1);
    }
  };
  const [statusFilter, setStatusFilter] = useState("active");
  const [descriptionWordCount, setDescriptionWordCount] = useState(0);
  const [inactiveEditModal, setInactiveEditModal] = useState({
    isOpen: false,
    partner: null,
  });
  const [resetPassword, setResetPassword] = useState(false);
  const [passwordResetModal, setPasswordResetModal] = useState({
    isOpen: false,
    currentPassword: "",
  });
  const [planDetails, setPlanDetails] = useState([
    {
      stripeProductId: "",
      amount: "",
      planName: "",
      couponDetails: "",
    },
  ]);
  const [logoPreviewModal, setLogoPreviewModal] = useState({
    isOpen: false,
    imageUrl: "",
    imageName: "",
  });
  const [viewDetailsOffcanvas, setViewDetailsOffcanvas] = useState({
    isOpen: false,
    partner: null,
  });
  const [flagLink, setFlagLink] = useState("us");
  const [countryCode, setCountryCode] = useState("+1");

  const logoFile = watch("logo");
  const selectedCountry = watch("country");
  
   const almataDescription = watch("almataSharedDiscription");

  useEffect(() => {
    if (almataDescription) {
      const words = almataDescription.trim().split(/\s+/).filter((word) => word.length > 0);
      setDescriptionWordCount(words.length);
    } else {
      setDescriptionWordCount(0);
    }
  }, [almataDescription]);
  
  const loadCountryOptions = async (inputValue) => {
    try {
      const response = inputValue
        ? await fetch(
            `https://restcountries.com/v3.1/name/${inputValue}?fields=name,currencies,cca2,cca3,ccn3,idd  `
          )
        : await fetch(
            "https://restcountries.com/v3.1/all?fields=name,currencies,cca2,cca3,ccn3,idd"
          );
      const data = await response.json();
      return data.slice(0, 10).map((country) => {
        const currency = Object.keys(country.currencies || {})[0] || "USD";
        return {
          value: country.name.common,
          label: country.name.common,
          isoCode: country.cca2,
          countryCode: country.idd.root
            ? country.idd.root +
              (country.idd.suffixes?.length
                ? country.name.common === "United States"
                  ? ""
                  : country?.idd?.suffixes?.[0]
                : "")
            : "",
        };
      });
    } catch (error) {
      return [];
    }
  };

  const handleCountryChange = (selectedOption, { action }) => {
    if (action === "clear") {
      setValue("country", "");
      setValue("currency", "");
      setFlagLink("us");
      setCountryCode("+1");
      return null;
    }
    setValue("country", selectedOption?.value || "");

    let currency = "USD";
    if (selectedOption?.value === "Japan") currency = "JPY";
    else if (selectedOption?.value === "Singapore") currency = "SGD";
    else if (selectedOption?.value === "Hong Kong") currency = "HKD";
    else if (selectedOption?.value === "Australia") currency = "AUD";
    else if (selectedOption?.value === "Austria") currency = "EUR";

    setValue("currency", currency);

    if (selectedOption) {
      setFlagLink(selectedOption.isoCode || "us");
      setCountryCode(selectedOption.countryCode || "+1");
    }

    return selectedOption;
  };

  useEffect(() => {
    dispatch(
      getAlmataPartners({
        limit: 10,
        page: currentPage,
        search: searchTerm,
        status: statusFilter,
      })
    );
  }, [dispatch, currentPage, searchTerm, statusFilter]);

  useEffect(() => {
    if (!editingPartner && !watch("country")) {
      setValue("country", "United States");
      setValue("currency", "USD");
      setFlagLink("us");
      setCountryCode("+1");
    }
  }, [editingPartner, setValue, watch]);

  const onSubmit = (data) => {
    const filteredIds = planDetails
      .map((plan) => plan.stripeProductId)
      .filter((id) => id && id.trim());

    const duplicates = filteredIds.filter(
      (id, index) => filteredIds.indexOf(id) !== index
    );
    if (duplicates.length > 0) {
      toast.error("Duplicate stripeProductId");
      return;
    }
    const adminId = JSON.parse(sessionStorage.getItem("user"))?._id;
    // Exclude planDetails, plainPassword, status, roleId, token, tokenExpire, verified, __v, and createdByModel from form data
    const {
      planDetails: _,
      plainPassword: __,
      status: ___,
      roleId: ____,
      token: _____,
      tokenExpire: ______,
      verified: _______,
      __v: ________,
      createdByModel: _________,
      ...formDataWithoutExcluded
    } = data;

    const payload = {
      ...formDataWithoutExcluded,
      stripeProductId: filteredIds,
      logo: selectedFile || (editingPartner ? editingPartner.logo : null),
      createdBy: adminId,
      countryCode,
      flagLink: `https://flagcdn.com/${flagLink?.toLowerCase()}.svg`,
      refreshParams: {
        limit: 10,
        page: currentPage,
        search: searchTerm,
        status: statusFilter,
      },
    };

    // Only add createdByModel for new partners (add case)
    if (!editingPartner) {
      payload.createdByModel = "user";
    }
    console.log(payload, "payload");
    if (editingPartner) {
      dispatch(editAlmataPartner({ id: editingPartner._id, ...payload }));
    } else {
      dispatch(addAlmataPartner(payload));
    }
  };

  const { success } = useSelector((state) => state.almata || {});

  useEffect(() => {
    if (success && !formLoading) {
      reset({
        companyName: "",
        email: "",
        firstName: "",
        lastName: "",
        contactNumber: "",
        logo: null,
        country: "",
        currency: "",
        status: "active",
        almataSharedDiscription: "",
      });
      setSelectedFileName("");
      setSelectedFile(null);
      setEditingPartner(null);
      setResetPassword(false);
      setPlanDetails([
        {
          stripeProductId: "",
          amount: "",
          planName: "",
          couponDetails: "",
        },
      ]);
      dispatch({ type: 'RESET_ALMATA_SUCCESS' });
    }
  }, [success, formLoading, resetPassword, reset, dispatch]);

  // Separate useEffect for password reset success
  useEffect(() => {
    if (passwordResetStatus === 200) {
      dispatch(
        getAlmataPartners({
          limit: 10,
          page: currentPage,
          search: searchTerm,
          status: statusFilter,
        })
      );
    }
  }, [passwordResetStatus, dispatch, currentPage, searchTerm, statusFilter]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setSelectedFileName(file.name);
    } else {
      setSelectedFile(null);
      setSelectedFileName("");
    }
  };

  const removePartner = (partner) => {
    setDeleteModal({ isOpen: true, partner });
  };

  const handleDeleteConfirm = () => {
    dispatch(
      deleteAlmataPartner({
        id: deleteModal.partner._id,
        refreshParams: {
          limit: 10,
          page: currentPage,
          search: searchTerm,
          status: statusFilter,
        },
      })
    );
    setDeleteModal({ isOpen: false, partner: null });
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, partner: null });
  };

  const handleStatusChange = (partner) => {
    const newStatus = partner.status === "active" ? "inactive" : "active";
    setStatusModal({ isOpen: true, partner, newStatus });
  };

  const handleStatusConfirm = () => {
    dispatch(
      changeAlmataStatus({
        _id: statusModal.partner._id,
        status: statusModal.newStatus,
        refreshParams: {
          limit: 10,
          page: currentPage,
          search: searchTerm,
          status: statusFilter,
        },
      })
    );
    setStatusModal({ isOpen: false, partner: null, newStatus: null });
  };

  const handleStatusCancel = () => {
    setStatusModal({ isOpen: false, partner: null, newStatus: null });
  };

  const handleEditClick = async (partner) => {
    if (partner.status === "inactive") {
      setInactiveEditModal({ isOpen: true, partner });
    } else {
      setEditingPartner(partner);
      reset({
        ...partner,
      });
      setShowInputForm(true);
      setResetPassword(false);

      if (partner.country) {
        try {
          const response = await fetch(
            `https://restcountries.com/v3.1/name/${partner.country}?fields=name,currencies,cca2,cca3,ccn3,idd`
          );
          const data = await response.json();
          if (data && data.length > 0) {
            const country = data[0];
            setFlagLink(country.cca2 || "us");
            setCountryCode(
              country.idd.root
                ? country.idd.root +
                    (country.idd.suffixes?.length
                      ? country.name.common === "United States"
                        ? ""
                        : country?.idd?.suffixes?.[0]
                      : "")
                : "+1"
            );
          }
        } catch (error) {
          console.error("Error fetching country data:", error);
          setFlagLink("us");
          setCountryCode("+1");
        }
      } else {
        setFlagLink("us");
        setCountryCode("+1");
      }

      setPlanDetails(
        partner.planDetails &&
          Array.isArray(partner.planDetails) &&
          partner.planDetails.length > 0
          ? partner.planDetails.map((plan) => ({
              stripeProductId: plan.stripeProductId || "",
              amount: plan.amount || "",
              planName: plan.planName || "",
              couponDetails: plan.couponDetails || "",
              currency: plan.currency || partner.currency || "USD",
            }))
          : [
              {
                stripeProductId: "",
                amount: "",
                planName: "",
                couponDetails: "",
              },
            ]
      );
    }
  };

  const handleInactiveEditClose = async () => {
    const partner = inactiveEditModal.partner;
    setInactiveEditModal({ isOpen: false, partner: null });
    if (partner) {
      setEditingPartner(partner);
      reset({
        ...partner,
      });
      setShowInputForm(true);
      setResetPassword(false);

      if (partner.country) {
        try {
          const response = await fetch(
            `https://restcountries.com/v3.1/name/${partner.country}?fields=name,currencies,cca2,cca3,ccn3,idd`
          );
          const data = await response.json();
          if (data && data.length > 0) {
            const country = data[0];
            setFlagLink(country.cca2 || "us");
            setCountryCode(
              country.idd.root
                ? country.idd.root +
                    (country.idd.suffixes?.length
                      ? country.name.common === "United States"
                        ? ""
                        : country?.idd?.suffixes?.[0]
                      : "")
                : "+1"
            );
          }
        } catch (error) {
          console.error("Error fetching country data:", error);
          setFlagLink("us");
          setCountryCode("+1");
        }
      } else {
        setFlagLink("us");
        setCountryCode("+1");
      }

      setPlanDetails(
        partner.planDetails &&
          Array.isArray(partner.planDetails) &&
          partner.planDetails.length > 0
          ? partner.planDetails.map((plan) => ({
              stripeProductId: plan.stripeProductId || "",
              amount: plan.amount || "",
              planName: plan.planName || "",
              couponDetails: plan.couponDetails || "",
              currency: plan.currency || partner.currency || "USD",
            }))
          : [
              {
                stripeProductId: "",
                amount: "",
                planName: "",
                couponDetails: "",
              },
            ]
      );
    }
  };

 const handlePasswordResetClick = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const currentPasswordValue = watch("plainPassword") || "";
    setPasswordResetModal({ isOpen: true, currentPassword: currentPasswordValue });
  };

  const handlePasswordResetSave = (newPassword) => {
    setValue("plainPassword", newPassword);
    setResetPassword(true);
    setPasswordResetModal({ isOpen: false, currentPassword: "" });
  };

  const handlePasswordResetClose = () => {
    setPasswordResetModal({ isOpen: false, currentPassword: "" });
    setResetPassword(false);
  };

  const handleLogoPreview = () => {
    let imageUrl = "";
    let imageName = "";

    if (selectedFile) {
      imageUrl = URL.createObjectURL(selectedFile);
      imageName = selectedFile.name;
    } else if (editingPartner?.logo) {
      imageUrl = editingPartner.logo;
      imageName = editingPartner.logo.split("/").pop();
    }

    setLogoPreviewModal({
      isOpen: true,
      imageUrl,
      imageName,
    });
  };

  const handleLogoPreviewClose = () => {
    setLogoPreviewModal({
      isOpen: false,
      imageUrl: "",
      imageName: "",
    });
  };

  const handleViewDetails = (partner) => {
    setViewDetailsOffcanvas({
      isOpen: true,
      partner,
    });
  };

  const handleTableRowClick = (partner) => {
    navigate("/almata-oem-end-users", { 
      state: { 
        partner,
        partnerPageNumber: currentPage,
        partnerStatus: statusFilter 
      } 
    });
  };
  const handleViewDetailsOffcanvasClose = () => {
    setViewDetailsOffcanvas({
      isOpen: false,
      partner: null,
    });
  };

  const handleResendVerification = (almataId) => {
    dispatch(resendAlmataVerificationEmail(almataId));
  };

  return (
    <div className="page-container">
      <div className="page-card scroll-container">
        <div className="form-header">
          <h2>Almata Partners Management</h2>
          <div
            onClick={() => setShowInputForm(!showInputForm)}
            className="cursor-pointer info_pointer toggle-btn fs-5 fw-bolder"
            title={showInputForm ? "Hide Input Form" : "Show Input Form"}
          >
            {showInputForm ? <FiMinusCircle /> : <FiPlusCircle />}
          </div>
        </div>
        {showInputForm && (
          <div className="input-form-section">
            <form onSubmit={handleSubmit(onSubmit)} className="input-form">
              <div className="form-grid">
                <div className="form-group">
                  <label>
                    Company Name <span className="text-danger">*</span>
                  </label>
                  <input
                    {...register("companyName", {
                      required: "Company name is required",
                      validate: (value) =>
                        value.trim() !== "" || "Company name is required",
                    })}
                    className={`form-input ${
                      errors.companyName ? "error" : ""
                    } ${
                      editingPartner?.status === "inactive" ? "disabled" : ""
                    }`}
                    placeholder="Enter company name"
                    disabled={editingPartner?.status === "inactive"}
                    onChange={(e) => {
                      const trimmedValue = e.target.value.replace(/\s+/g, " ");
                      setValue("companyName", trimmedValue);
                    }}
                    style={{
                      backgroundColor:
                        editingPartner?.status === "inactive" ? "#f8f9fa" : "",
                    }}
                  />
                  {errors.companyName && (
                    <span className="error-text">
                      {errors.companyName.message}
                    </span>
                  )}
                </div>

                <div className="form-group" style={{ position: "relative" }}>
                  <label>
                    Email <span className="text-danger">*</span>{" "}
                  </label>
                  <input
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value:
                          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                        message: "Please enter a valid email address",
                      },
                    })}
                    type="email"
                    className={`form-input ${errors.email ? "error" : ""} ${
                      editingPartner?.status === "inactive" ? "disabled" : ""
                    }`}
                    placeholder="Enter email"
                    disabled={
                      editingPartner?.status === "inactive" || editingPartner
                    }
                    style={{
                      backgroundColor:
                        editingPartner?.status === "inactive"
                          ? "#f8f9fa"
                          : "" || editingPartner
                          ? "#f8f9fa"
                          : "",
                      paddingRight: "35px",
                    }}
                  />
                  {editingPartner &&
                    (editingPartner.verified ? (
                      <MdVerified
                        className="position-absolute"
                        style={{
                          right: "5px",
                          top: "66%",
                          transform: "translateY(-50%)",
                          color: "#28a745",
                          fontSize: "18px",
                          cursor: "pointer",
                        }}
                        title="Email Verified"
                      />
                    ) : (
                      <GoUnverified
                        className="position-absolute"
                        style={{
                          right: "5px",
                          top: "66%",
                          transform: "translateY(-50%)",
                          color: "#dc3545",
                          fontSize: "18px",
                          cursor: "pointer",
                        }}
                        title="Email Not Verified"
                      />
                    ))}
                  {errors.email && (
                    <span className="error-text">{errors.email.message}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>
                    First Name <span className="text-danger">*</span>
                  </label>
                  <input
                    {...register("firstName", {
                      required: "First name is required",
                      validate: (value) =>
                        value.trim() !== "" || "First name is required",
                    })}
                    className={`form-input ${errors.firstName ? "error" : ""} ${
                      editingPartner?.status === "inactive" ? "disabled" : ""
                    }`}
                    placeholder="Enter first name"
                    disabled={editingPartner?.status === "inactive"}
                    style={{
                      backgroundColor:
                        editingPartner?.status === "inactive" ? "#f8f9fa" : "",
                    }}
                  />
                  {errors.firstName && (
                    <span className="error-text">
                      {errors.firstName.message}
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label>
                    Last Name <span className="text-danger">*</span>
                  </label>
                  <input
                    {...register("lastName", {
                      required: "Last name is required",
                      validate: (value) =>
                        value.trim() !== "" || "Last name is required",
                    })}
                    className={`form-input ${errors.lastName ? "error" : ""} ${
                      editingPartner?.status === "inactive" ? "disabled" : ""
                    }`}
                    placeholder="Enter last name"
                    disabled={editingPartner?.status === "inactive"}
                    style={{
                      backgroundColor:
                        editingPartner?.status === "inactive" ? "#f8f9fa" : "",
                    }}
                  />
                  {errors.lastName && (
                    <span className="error-text">
                      {errors.lastName.message}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label>
                    Country/Currency <span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="country"
                    control={control}
                    rules={{ required: "Country is required" }}
                    render={({ field: { onChange, value, ...field } }) => (
                      <AsyncSelect
                        {...field}
                        value={
                          value
                            ? {
                                value,
                                label: `${value} (${
                                  watch("currency") || "USD"
                                })`,
                              }
                            : null
                        }
                        loadOptions={loadCountryOptions}
                        onChange={(selectedOption, action) => {
                          const result = handleCountryChange(
                            selectedOption,
                            action
                          );
                          onChange(selectedOption?.value || "");
                        }}
                        placeholder="Search country..."
                        className={errors.country ? "error" : ""}
                        classNamePrefix="react-select"
                        isClearable
                        defaultOptions
                        noOptionsMessage={() => "No countries found"}
                        loadingMessage={() => "Loading countries..."}
                        isDisabled={editingPartner?.status === "inactive"}
                        styles={{
                          control: (base, state) => ({
                            ...base,
                            minHeight: "30px",
                            height: "30px",
                            backgroundColor:
                              editingPartner?.status === "inactive"
                                ? "#f8f9fa"
                                : base.backgroundColor,
                            border: errors.country
                              ? "1px solid #dc3545"
                              : "1px solid #ddd",
                            borderRadius: "4px",
                            "&:hover": {
                              border: errors.country
                                ? "1px solid #dc3545"
                                : "1px solid #ddd",
                            },
                          }),
                        }}
                      />
                    )}
                  />
                  <input {...register("currency")} type="hidden" />
                  {errors.country && (
                    <span className="error-text">{errors.country.message}</span>
                  )}
                </div>
                <div className="form-group" style={{ position: "relative" }}>
                  <label>
                    Contact Number <span className="text-danger">*</span>
                  </label>

                  <div style={{ position: "relative" }}>
                    {flagLink && countryCode && (
                      <div
                        className="position-absolute d-flex align-items-center"
                        style={{
                          left: "8px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          zIndex: 2,
                          borderRight: "1px solid #ddd",
                          paddingRight: "8px",
                          height: "24px",
                          gap: "4px",
                          background: "#fff",
                        }}
                      >
                        <img
                          src={`https://flagcdn.com/${flagLink?.toLowerCase()}.svg`}
                          alt="flag"
                          width={20}
                          height={14}
                          style={{ objectFit: "cover" }}
                        />
                        <span style={{ fontSize: "12px", color: "#666" }}>
                          ({countryCode})
                        </span>
                      </div>
                    )}

                    <input
                      {...register("contactNumber", {
                        required: "Contact number is required",
                        validate: (value) => {
                          const result = validatePhoneNumber(
                            value,
                            selectedCountry
                          );
                          return result === true ? true : result;
                        },
                      })}
                      type="tel"
                      className={`form-input ${
                        errors.contactNumber ? "error" : ""
                      } ${
                        editingPartner?.status === "inactive" ? "disabled" : ""
                      }`}
                      placeholder="Enter contact number"
                      maxLength={(() => {
                        const rules = getPhoneValidationRules(selectedCountry);
                        return rules.max;
                      })()}
                      onInput={(e) => {
                        let value = e.target.value.replace(/[^0-9]/g, "");
                        const rules = getPhoneValidationRules(selectedCountry);

                        // Prevent input beyond max length
                        if (value.length > rules.max) {
                          value = value.slice(0, rules.max);
                        }

                        e.target.value = value;
                        setValue("contactNumber", value);
                      }}
                      disabled={editingPartner?.status === "inactive"}
                      style={{
                        paddingLeft: flagLink && countryCode ? "85px" : "12px",
                        height: "30px",
                        border: errors.contactNumber
                          ? "1px solid #dc3545"
                          : "1px solid #ddd",
                        borderRadius: "4px",
                        backgroundColor:
                          editingPartner?.status === "inactive"
                            ? "#f8f9fa"
                            : "#fff",
                        width: "100%",
                      }}
                    />
                  </div>

                  {errors.contactNumber && (
                    <span
                      className="error-text"
                      style={{
                        position: "absolute",
                        bottom: "-18px",
                        left: "0",
                        fontSize: "12px",
                        color: errors.contactNumber.message.includes("/")
                          ? "#ef4444"
                          : "#ef4444",
                      }}
                    >
                      {errors.contactNumber.message.includes("/")
                        ? `Digits entered: ${errors.contactNumber.message}`
                        : errors.contactNumber.message}
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label>Logo</label>
                  <div className="file-input-wrapper d-flex align-items-center">
                    <div className="flex-grow-1 position-relative">
                      <input
                        type="file"
                        accept="image/*"
                        className="file-input"
                        id="logo-upload"
                        onChange={handleFileChange}
                        disabled={editingPartner?.status === "inactive"}
                        style={{
                          backgroundColor:
                            editingPartner?.status === "inactive"
                              ? "#f8f9fa"
                              : "",
                        }}
                      />
                      <label htmlFor="logo-upload" className="file-input-label">
                        <span>
                          {(() => {
                            const fileName =
                              selectedFileName ||
                              (editingPartner?.logo
                                ? editingPartner.logo.split("/").pop()
                                : "Upload Image");
                            return fileName.length > 22
                              ? fileName.substring(0, 22) + "..."
                              : fileName;
                          })()}
                        </span>
                        {!(selectedFile || editingPartner?.logo) && (
                          <FiUploadCloud className="upload-icon" />
                        )}
                      </label>

                      {(selectedFile || editingPartner?.logo) && (
                        <MdVisibility
                          className="info_pointer text-secondary text-primary"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleLogoPreview();
                          }}
                          title="Preview Logo"
                          style={{
                            position: "absolute",
                            right: "10px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            fontSize: "22px",
                            background: "white",
                            padding: "4px",
                            borderRadius: "4px",
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    Status <span className="text-danger">*</span>
                  </label>
                  <select
                    {...register("status", {
                      required: "Status is required",
                    })}
                    className="form-input disabled"
                    disabled
                    value={editingPartner ? watch("status") : "active"}
                    style={{
                      backgroundColor: "#f8f9fa",
                    }}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  {errors.status && (
                    <span className="error-text">{errors.status.message}</span>
                  )}
                </div>

                {editingPartner && (
                  <div className="form-group position-relative">
                    <label>Password</label>
                    <input
                      {...register("plainPassword")}
                      type="text"
                      className="form-input"
                      placeholder="Generated password"
                      style={{
                        backgroundColor:
                          editingPartner?.status === "inactive"
                            ? "#f8f9fa"
                            : "",
                      }}
                      readOnly
                    />
                    {editingPartner?.status === "active" && (
                      <MdRefresh
                        className="position-absolute"
                        style={{
                          right: "8px",
                          top: "66%",
                          transform: "translateY(-50%)",
                          cursor: "pointer",
                          color: "#007bff",
                          fontSize: "16px",
                          zIndex: 10,
                        }}
                        onClick={(e) => handlePasswordResetClick(e)}
                        title="Reset password"
                      />
                    )}
                  </div>
                )}
              </div>
              <div className="form-group col-4 mb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <label>
                      Almata Shared Description
                      <span className="text-danger">*</span>
                      <span
                        className="text-secondary info_pointer cursor-pointer ms-1"
                        title="You can enter up to 50 words"
                      >
                        <FiInfo />
                      </span>
                    </label>
                  </div>

                  <div className="text-muted small mt-1">
                    Words: {descriptionWordCount}/50
                  </div>
                </div>

                <textarea
                  {...register("almataSharedDiscription", {
                    required: "Almata shared description is required",
                  })}
                  className={`form-input ${
                    errors.almataSharedDiscription ? "error" : ""
                  } ${editingPartner?.status === "inactive" ? "disabled" : ""}`}
                  placeholder="Enter Almata VOC...."
                  rows={4}
                  disabled={editingPartner?.status === "inactive"}
                  style={{
                    backgroundColor:
                      editingPartner?.status === "inactive" ? "#f8f9fa" : "",
                  }}
                  onChange={(e) => {
                    const words = e.target.value
                      .trim()
                      .split(/\s+/)
                      .filter((word) => word.length > 0);
                    setDescriptionWordCount(words.length);
                    if (e.target.value.trim()) {
                      clearErrors("almataSharedDiscription");
                    }
                  }}
                  onKeyDown={(e) => {
                    const currentText = e.target.value;
                    const words = currentText
                      .trim()
                      .split(/\s+/)
                      .filter((word) => word.length > 0);
                    if (
                      words.length >= 50 &&
                      e.key !== "Backspace" &&
                      e.key !== "Delete" &&
                      !e.key.includes("Arrow")
                    ) {
                      e.preventDefault();
                      toast.error("Maximum 50 words reached");
                    }
                  }}
                  onPaste={(e) => {
                    const currentText = e.target.value;
                    const pastedText = e.clipboardData.getData("text");
                    const combinedText = currentText + pastedText;
                    const words = combinedText
                      .trim()
                      .split(/\s+/)
                      .filter((word) => word.length > 0);
                    if (words.length > 50) {
                      e.preventDefault();
                      toast.error("Cannot paste - would exceed 50 word limit");
                    }
                  }}
                />
                {errors.almataSharedDiscription && (
                  <span className="error-text">
                    {errors.almataSharedDiscription.message}
                  </span>
                )}
              </div>

              <PlansDetails
                planDetails={planDetails}
                setPlanDetails={setPlanDetails}
                register={register}
                setValue={setValue}
                clearErrors={clearErrors}
                errors={errors}
                editingPartner={editingPartner}
              />

              <div className="form-actions">
                {editingPartner && (
                  <button
                    type="button"
                    className="cancel-btn-deleteModal"
                    onClick={() => {
                      setEditingPartner(null);
                      reset({
                        companyName: "",
                        email: "",
                        firstName: "",
                        lastName: "",
                        contactNumber: "",
                        logo: null,
                        country: "",
                        currency: "",
                        status: "active",
                        almataSharedDiscription: "",
                      });
                      setSelectedFileName("");
                      setSelectedFile(null);
                      setPlanDetails([
                        {
                          stripeProductId: "",
                          amount: "",
                          planName: "",
                          couponDetails: "",
                        },
                      ]);
                    }}
                  >
                    Reset
                  </button>
                )}
                <button
                  type="submit"
                  className="add-btn"
                  disabled={
                    formLoading || editingPartner?.status === "inactive"
                  }
                >
                  {formLoading
                    ? "Processing..."
                    : editingPartner
                    ? "Update Almata Partner"
                    : "Add Almata Partner"}
                </button>
              </div>
            </form>
          </div>
        )}
        <AlmataPartnerTable
          partners={partners}
          loading={loading}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          searchTerm={searchTerm}
          handleSearchChange={handleSearchChange}
          copiedId={copiedId}
          setCopiedId={setCopiedId}
          handleViewDetails={handleViewDetails}
          handleTableRowClick={handleTableRowClick}
          handleEditClick={handleEditClick}
          removePartner={removePartner}
          handleStatusChange={handleStatusChange}
          currentPage={currentPage}
          pagination={pagination}
          setCurrentPage={setCurrentPage}
          handleResendVerification={handleResendVerification}
        />
      </div>

      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        partnerName={deleteModal.partner?.companyName || ""}
      />

      <StatusConfirmModal
        isOpen={statusModal.isOpen}
        onClose={handleStatusCancel}
        onConfirm={handleStatusConfirm}
        partnerName={statusModal.partner?.companyName || ""}
        currentStatus={statusModal.partner?.status || ""}
        newStatus={statusModal.newStatus || ""}
      />

      <InactiveEditModal
        isOpen={inactiveEditModal.isOpen}
        onClose={handleInactiveEditClose}
        partnerName={inactiveEditModal.partner?.companyName || ""}
      />

      <PasswordResetModal
        isOpen={passwordResetModal.isOpen}
        onClose={handlePasswordResetClose}
        onSave={handlePasswordResetSave}
        currentPassword={passwordResetModal.currentPassword}
        partnerId={editingPartner?._id}
        partnerType="almata"
      />

      <LogoPreviewModal
        isOpen={logoPreviewModal.isOpen}
        onClose={handleLogoPreviewClose}
        imageUrl={logoPreviewModal.imageUrl}
        imageName={logoPreviewModal.imageName}
      />

      {/* Eye Icon Offcanvas - Partner Details */}
      <Offcanvas
        show={viewDetailsOffcanvas.isOpen}
        onHide={handleViewDetailsOffcanvasClose}
        placement="end"
        style={{ width: "500px" }}
      >
        <Offcanvas.Header className="border-bottom">
          <Offcanvas.Title className="d-flex align-items-center" style={{fontSize:"15px"}}>
            Almata Partner Details
          </Offcanvas.Title>
          <button
            type="button"
            className="btn-close"
            onClick={handleViewDetailsOffcanvasClose}
            aria-label="Close"
          />
        </Offcanvas.Header>

        <Offcanvas.Body className="p-0">
          {viewDetailsOffcanvas.partner && (
            <>
              {/* Header Section with Logo and Status */}
              <div className="bg-light p-3 border-bottom">
                <div className="d-flex align-items-center">
                  {viewDetailsOffcanvas.partner.logo && (
                    <img
                      src={viewDetailsOffcanvas.partner.logo}
                      alt="Company Logo"
                      className="rounded me-3"
                      style={{
                        height: "60px",
                        objectFit: "cover",
                        border: "1px solid #ddd",
                        background: "linear-gradient(135deg, #1e40af, #3b82f6)",
                      }}
                    />
                  )}
                  <div>
                    <h6 className="mb-0">
                      {viewDetailsOffcanvas.partner.companyName}
                    </h6>
                    <span
                      className={`badge ${
                        viewDetailsOffcanvas.partner.status === "active"
                          ? "bg-success"
                          : "bg-secondary"
                      }`}
                    >
                      {viewDetailsOffcanvas.partner.status?.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="p-3 border-bottom">
                <h6 className="text-muted mb-3">Contact Information</h6>
                <div className="row g-1">
                  <div className="col-12">
                    <div className="d-flex align-items-center">
                      <MdEmail
                        className="text-muted me-2"
                        style={{ fontSize: "12px" }}
                      />
                      <span>{viewDetailsOffcanvas.partner.email}</span>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="d-flex align-items-center">
                      <span
                        className="text-muted me-2"
                        style={{ fontSize: "10px" }}
                      >
                        👤
                      </span>
                      <span>
                        {viewDetailsOffcanvas.partner.firstName}{" "}
                        {viewDetailsOffcanvas.partner.lastName}
                      </span>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="d-flex align-items-center">
                      <MdPhone
                        className="text-muted me-2"
                        style={{ fontSize: "12px" }}
                      />
                      {viewDetailsOffcanvas.partner.countryCode ? (
                        <div className="d-flex align-items-center">
                          <span style={{ fontSize: "", marginRight: "4px" }}>
                            ({viewDetailsOffcanvas.partner.countryCode})
                          </span>
                          <span>
                            {viewDetailsOffcanvas.partner.contactNumber}
                          </span>
                        </div>
                      ) : (
                        <span>
                          {viewDetailsOffcanvas.partner.contactNumber}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="d-flex align-items-center">
                      <MdLocationOn
                        className="text-muted me-2"
                        style={{ fontSize: "14px" }}
                      />
                      <span>
                        {viewDetailsOffcanvas.partner.country} (
                        {viewDetailsOffcanvas.partner.currency})
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Plan Details */}
              {viewDetailsOffcanvas.partner.planDetails &&
                viewDetailsOffcanvas.partner.planDetails.length > 0 && (
                  <div className="p-3 border-bottom">
                    <h6 className="text-muted mb-3">Plan Details</h6>
                    {viewDetailsOffcanvas.partner.planDetails.map(
                      (plan, index) => (
                        <div key={index} className="card mb-2">
                          <div className="card-body p-3">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <h6 className="card-title mb-0">
                                {plan.planName}
                              </h6>
                              <span className="badge bg-primary">
                                {plan.currency} {plan.amount / 100}
                              </span>
                            </div>
                            <small className="text-muted">
                              Product ID: {plan.stripeProductId}
                            </small>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                )}

              {/* Description */}
              <div className="p-3 border-bottom">
                <h6 className="text-muted mb-3">
                  <MdDescription className="me-2" />
                  Almata Shared Description
                </h6>
                <p className="mb-0">
                  {viewDetailsOffcanvas.partner.almataSharedDiscription ||
                    "No description available"}
                </p>
              </div>

              {/* Payment Information */}
              <div className="p-3 border-bottom">
                <h6 className="text-muted mb-3">
                  <MdAttachMoney className="me-2" />
                  Payment Information
                </h6>
                <div className="row g-2">
                  <div className="col-6">
                    <small className="text-muted">Payment Currency</small>
                    <div>{viewDetailsOffcanvas.partner.currency}</div>
                  </div>
                  <div className="col-6">
                    <small className="text-muted">Password</small>
                    <div className="d-flex align-items-center">
                      <span className="me-2">••••••••••••</span>
                      <small className="text-success">Available</small>
                    </div>
                  </div>
                </div>
              </div>

              {/* Timestamps */}
              <div className="p-3">
                <h6 className="text-muted mb-3">
                  <MdCalendarToday className="me-2" />
                  Timeline
                </h6>
                <div className="row g-2">
                  <div className="col-12">
                    <small className="text-muted">Created At</small>
                    <div>
                      {new Date(
                        viewDetailsOffcanvas.partner.createdAt
                      ).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                  <div className="col-12">
                    <small className="text-muted">Last Updated</small>
                    <div>
                      {new Date(
                        viewDetailsOffcanvas.partner.updatedAt
                      ).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
};

export default AlmataPartner;
